import { queryOptions } from "@tanstack/react-query";
import type { Layout, MediaSource, MediaUrls, MediaVariants, MediaView, PersonInput, PublishCheck, ResolvedLayout, Role, WorkInput, WorkStatus } from "@manos/core";

const BASE = (import.meta.env.VITE_API_URL ?? "/api").replace(/\/$/, "");

export class ApiError extends Error {
  constructor(public status: number, message: string, public body: Record<string, unknown> | null = null) {
    super(message);
  }
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, headers, ...rest } = init;
  const res = await fetch(BASE + path, {
    credentials: "include",
    ...rest,
    headers: { ...(json !== undefined ? { "content-type": "application/json" } : {}), ...headers },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as ({ error?: string } & Record<string, unknown>) | null;
    throw new ApiError(res.status, body?.error ?? `Request failed (${res.status})`, body);
  }
  return res.json() as Promise<T>;
}

export const apiUrl = (path: string) => BASE + path;

/* ---------- Types returned by the API ---------- */
export type User = { id: string; email: string; name: string | null; avatarUrl: string | null; role: Role };
export type Localized = Partial<Record<"en" | "es" | "ja" | "zh", string>>;
export type Category = { id: string; slug: string; name: Localized; position: number; works: number };
export type PageSummary = { id: string; slug: string; title: Localized; publishedAt: string | null; updatedAt: string; blocks: number; unpublishedChanges: boolean };
export type PageDetail = { id: string; slug: string; title: Localized; draft: Layout; published: Layout | null; publishedAt: string | null; updatedAt: string; version: number };
export type ContactMessage = { id: string; name: string; email: string; message: string; locale: string; readAt: string | null; emailedAt: string | null; createdAt: string };
export type TeamMember = { id: string; email: string; name: string | null; role: Role; lastSeenAt: string | null; disabledAt: string | null };

export type Media = {
  id: string;
  kind: "image" | "video";
  status: "uploading" | "processing" | "ready" | "failed";
  ownerId: string | null;
  originalFilename: string;
  contentType: string;
  bytes: number;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  fps: number | null;
  hasAudio: boolean | null;
  blurDataUrl: string | null;
  dominantColor: string | null;
  focalX: number;
  focalY: number;
  alt: Localized;
  watermark: boolean;
  variants: MediaVariants | null;
  source: MediaSource | null;
  progress: number;
  stage: string | null;
  error: string | null;
  createdAt: string;
  updatedAt: string;
  urls: MediaUrls | null;
};

/* ---------- Queries ---------- */
/** The signed-in user, or null when signed out. Other failures (server unreachable) throw. */
export const meQuery = queryOptions({
  queryKey: ["me"],
  queryFn: () =>
    api<{ user: User }>("/auth/me")
      .then((r): User | null => r.user)
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }),
  staleTime: 5 * 60_000,
  retry: 2,
});
export const providersQuery = queryOptions({
  queryKey: ["auth-providers"],
  queryFn: () => api<{ google: boolean; dev: boolean }>("/auth/providers"),
  staleTime: Infinity,
});
export const overviewQuery = queryOptions({
  queryKey: ["overview"],
  queryFn: () => api<{ unread: number }>("/studio/overview"),
  refetchInterval: 60_000,
});
export const categoriesQuery = queryOptions({
  queryKey: ["categories"],
  queryFn: () => api<{ categories: Category[] }>("/studio/categories").then((r) => r.categories),
});
export const pagesQuery = queryOptions({
  queryKey: ["pages"],
  queryFn: () => api<{ pages: PageSummary[] }>("/studio/pages").then((r) => r.pages),
});
export const inboxQuery = queryOptions({
  queryKey: ["inbox"],
  queryFn: () => api<{ messages: ContactMessage[] }>("/studio/inbox").then((r) => r.messages),
});
export const teamQuery = queryOptions({
  queryKey: ["team"],
  queryFn: () => api<{ team: TeamMember[] }>("/studio/team").then((r) => r.team),
});

const busy = (m: Media) => m.status === "uploading" || m.status === "processing";
export const mediaQuery = queryOptions({
  queryKey: ["media"],
  queryFn: () => api<{ media: Media[] }>("/studio/media").then((r) => r.media),
  // Poll while anything is uploading or processing, so progress feels live.
  refetchInterval: (q) => (q.state.data?.some(busy) ? 1500 : false),
});

/* ---------- Works, people, pages ---------- */
export type WorkSummary = {
  id: string; slug: string; title: string; status: WorkStatus; client: string | null; year: number | null;
  category: { id: string; name: Localized } | null; ownerId: string | null; owner: string | null;
  updatedAt: string; publishedAt: string | null; thumb: MediaView | null;
  hasUnpublishedChanges: boolean; reviewRequestedAt: string | null;
};
export type MediaRef = {
  id: string; kind: "image" | "video"; status: Media["status"]; originalFilename: string; width: number | null; height: number | null;
  durationMs: number | null; blurDataUrl: string | null; dominantColor: string | null; focalX: number; focalY: number;
  progress: number; stage: string | null; view: MediaView | null;
};
export type WorkDetail = {
  work: {
    id: string; status: WorkStatus; ownerId: string | null; publishedAt: string | null; version: number; updatedAt: string; data: WorkInput;
    live: WorkInput | null; liveUpdatedAt: string | null; hasUnpublishedChanges: boolean; reviewRequestedAt: string | null;
  };
  media: MediaRef[];
  checks: PublishCheck[];
  changed: string[];
};
export type PersonSummary = { id: string; slug: string; name: string; title: Localized; published: boolean; updatedAt: string; works: number; portrait: MediaView | null };
export type PersonDetail = {
  person: PersonInput & { id: string; version: number; updatedAt: string };
  portrait: MediaView | null;
  works: { id: string; title: string; status: WorkStatus; role: Localized }[];
};
export type Version = { id: string; reason?: string; createdAt: string; by: string | null; blocks?: number };

export const worksQuery = queryOptions({
  queryKey: ["works"],
  queryFn: () => api<{ works: WorkSummary[] }>("/studio/works").then((r) => r.works),
});
export const workQuery = (id: string) =>
  // The editor keeps this cache current on every save, so background refetches never surprise it.
  queryOptions({ queryKey: ["work", id], queryFn: () => api<WorkDetail>(`/studio/works/${id}`), refetchOnWindowFocus: false });
export const workVersionsQuery = (id: string) =>
  queryOptions({ queryKey: ["work", id, "versions"], queryFn: () => api<{ versions: Version[] }>(`/studio/works/${id}/versions`).then((r) => r.versions) });
export const peopleQuery = queryOptions({
  queryKey: ["people"],
  queryFn: () => api<{ people: PersonSummary[] }>("/studio/people").then((r) => r.people),
});
export const personQuery = (id: string) =>
  queryOptions({ queryKey: ["person", id], queryFn: () => api<PersonDetail>(`/studio/people/${id}`), staleTime: Infinity });
export const pageQuery = (slug: string) =>
  queryOptions({ queryKey: ["page", slug], queryFn: () => api<{ page: PageDetail; resolved: ResolvedLayout }>(`/studio/pages/${slug}`), staleTime: Infinity });
export const pageVersionsQuery = (slug: string) =>
  queryOptions({ queryKey: ["page", slug, "versions"], queryFn: () => api<{ versions: Version[] }>(`/studio/pages/${slug}/versions`).then((r) => r.versions) });
export const capabilitiesQuery = queryOptions({
  queryKey: ["capabilities"],
  queryFn: () => api<{ translate: boolean }>("/studio/capabilities"),
  staleTime: Infinity,
});

export type ShareLink = { id: string; label: string; hasPassword: boolean; expiresAt: string | null; revokedAt: string | null; createdAt: string };
export const shareLinksQuery = (workId: string) =>
  queryOptions({ queryKey: ["work", workId, "links"], queryFn: () => api<{ links: ShareLink[] }>(`/studio/works/${workId}/links`).then((r) => r.links) });
